import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import {
  DocumentNode,
  NetworkStatus,
  QueryHookOptions,
  QueryResult,
  useApolloClient,
} from '@apollo/client';

import { getError, getTypeOrNull } from './apollo';

type QueryVariables = {
  filter?: string | null;
  pageSize?: number | null;
  pageToken?: string | null;
  orderBy?: string | null;
};

type QueryOptions<
  TCustomVars extends Record<string, unknown>,
  TQueryVariables extends Record<string, unknown>,
> = {
  query: DocumentNode;
  typename: string;
  fetchPolicy?: QueryHookOptions['fetchPolicy'];
  mapVariables?: (variables: QueryVariables & TCustomVars) => TQueryVariables;
};

type InitialData<TItem> = {
  items: TItem[] | null | undefined;
  nextPageToken: string | null | undefined;
};

export type InitialListData<TItem> = InitialData<TItem>;

export type StandardListHookOptions<
  TCustomVars = QueryVariables,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TItem default propagates from caller's TData[TPath]; unknown breaks downstream consumers that index into items
  TItem = any,
> = {
  filter?: string;
  pageSize?: number;
  orderBy?: string;
  skip?: boolean;
  pollInterval?: number;
  fetchPolicy?: QueryHookOptions['fetchPolicy'];
  initialData?: InitialData<TItem>;
} & TCustomVars;

type ListResponse<TItem> = {
  flags: TItem[];
  nextPageToken: string | null;
};

// The non-error arm of the response union at the query's data path.
type ListSuccessResponse<TData, TPath extends keyof TData> = Exclude<
  TData[TPath],
  { __typename: 'Error' } | null | undefined
>;

type StandardListHookResult<TItem, TPath extends string, TResponse> = {
  [K in TPath]: TItem[] | undefined;
} & {
  hasNextPage: boolean;
  error?: Error;
  isLoading: boolean;
  isEmpty: boolean;
  nextPage: () => Promise<void>;
  isLoadingNextPage: boolean;
  refetch: () => Promise<unknown>;
  // The raw list response, for response-level fields the mapped result does
  // not surface (e.g. truncation metadata).
  response: TResponse | null;
};

// Overload for without subscription
export function withStandardListHook<
  /* eslint-disable @typescript-eslint/no-explicit-any -- TData/TCustomVars accept Apollo-generated types whose Variables shapes don't satisfy a stricter Record<string, unknown> constraint */
  TData extends Record<string, any>,
  TItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = QueryVariables,
  TQueryVariables extends Record<string, any> = QueryVariables & TCustomVars,
  /* eslint-enable @typescript-eslint/no-explicit-any */
>(
  useQuery: (
    options?: QueryHookOptions<TData, TQueryVariables>,
  ) => QueryResult<TData, TQueryVariables>,
  dataPath: TPath,
  queryOptions?: QueryOptions<TCustomVars, TQueryVariables>,
): (
  options: StandardListHookOptions<TCustomVars, TItem>,
) => StandardListHookResult<TItem, TPath, ListSuccessResponse<TData, TPath>>;

// Implementation
export function withStandardListHook<
  /* eslint-disable @typescript-eslint/no-explicit-any -- TData/TCustomVars accept Apollo-generated types whose Variables shapes don't satisfy a stricter Record<string, unknown> constraint */
  TData extends Record<string, any>,
  TItem,
  TPath extends Extract<keyof TData, string>,
  TCustomVars extends Record<string, any> = QueryVariables,
  TQueryVariables extends Record<string, any> = QueryVariables & TCustomVars,
  /* eslint-enable @typescript-eslint/no-explicit-any */
>(
  useQuery: (
    options?: QueryHookOptions<TData, TQueryVariables>,
  ) => QueryResult<TData, TQueryVariables>,
  dataPath: TPath,
  queryOptions?: QueryOptions<TCustomVars, TQueryVariables>,
) {
  return (
    options: StandardListHookOptions<TCustomVars, TItem>,
  ): StandardListHookResult<
    TItem,
    TPath,
    ListSuccessResponse<TData, TPath>
  > => {
    const {
      filter,
      pageSize,
      orderBy,
      skip,
      initialData,
      pollInterval,
      fetchPolicy,
      ...customVars
    } = options;

    const client = useApolloClient();
    const writtenQuery = useRef<boolean>(false);
    const customVarsRef = useRef(customVars);
    customVarsRef.current = customVars;
    const mapVariables = useCallback(
      (variables: QueryVariables & TCustomVars): TQueryVariables =>
        queryOptions?.mapVariables?.(variables) ??
        (variables as TQueryVariables),
      [],
    );

    useLayoutEffect(() => {
      if (queryOptions?.query && initialData && !writtenQuery.current) {
        const variables = mapVariables({
          filter: filter ?? null,
          pageSize: pageSize ?? null,
          orderBy: orderBy ?? null,
          ...(customVars as TCustomVars),
        });

        const existing = client.cache.readQuery({
          query: queryOptions.query,
          variables,
        });

        if (existing) {
          // We only write the query if it doesn't exist in the cache.
          return;
        }

        client.cache.writeQuery({
          query: queryOptions.query,
          variables,
          data: {
            [dataPath]: {
              __typename: queryOptions.typename,
              [dataPath]: initialData.items,
              nextPageToken: initialData.nextPageToken,
            },
          },
        });

        // We do not want to write the query again even if initial data
        // or options change.
        writtenQuery.current = true;
      }
    }, [
      initialData,
      filter,
      pageSize,
      orderBy,
      customVars,
      client,
      mapVariables,
    ]);

    const variables = mapVariables({
      filter: filter ?? null,
      pageSize: pageSize ?? null,
      orderBy: orderBy ?? null,
      ...(customVars as TCustomVars),
    });

    const {
      data,
      error: networkError,
      loading,
      fetchMore,
      refetch,
      networkStatus,
    } = useQuery({
      variables,
      pollInterval,
      skip,
      fetchPolicy:
        fetchPolicy ??
        queryOptions?.fetchPolicy ??
        (initialData ? 'cache-first' : undefined),
      notifyOnNetworkStatusChange: true,
    });

    // Get the nested data using the provided path
    const nestedData = data?.[dataPath];
    const typedData = getTypeOrNull(nestedData) as ListResponse<TItem> | null;
    const appError = getError(nestedData) ?? undefined;

    // @ts-expect-error dataPath is a dynamic key not present in the ListResponse type
    const loadedItems = typedData?.[dataPath] as TItem[] | undefined;
    const loadedToken = typedData?.nextPageToken;

    const [{ nextPageToken, items, isLoading, isLoadingNextPage }, setState] =
      useState<{
        isLoading: boolean;
        isLoadingNextPage: boolean;
        items?: TItem[];
        nextPageToken: string | null | undefined;
      }>({
        isLoading: loading,
        items: loadedItems ?? undefined,
        nextPageToken: typedData?.nextPageToken ?? null,
        isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
      });

    const handleNextPage = useCallback(async () => {
      if (nextPageToken && fetchMore) {
        await fetchMore({
          variables: mapVariables({
            pageToken: nextPageToken,
            ...(customVarsRef.current as TCustomVars),
          }),
        });
      }
    }, [nextPageToken, fetchMore, mapVariables]);

    useEffect(() => {
      if (loading) {
        setState(current => ({
          ...current,
          isLoading: true,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
        }));
      } else if (loadedItems) {
        setState(current => ({
          ...current,
          items: loadedItems,
          isLoading: false,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
          nextPageToken: loadedToken,
        }));
      } else {
        setState(current => ({
          ...current,
          isLoading: false,
          isLoadingNextPage: networkStatus === NetworkStatus.fetchMore,
        }));
      }
    }, [loading, loadedItems, loadedToken, networkStatus]);

    const isEmpty = items?.length === 0 && !appError && !networkError;

    return {
      [dataPath]: items,
      hasNextPage: !!nextPageToken,
      error: appError ?? networkError,
      isLoading,
      nextPage: handleNextPage,
      isLoadingNextPage,
      isEmpty,
      refetch,
      response: typedData as ListSuccessResponse<TData, TPath> | null,
    } as StandardListHookResult<
      TItem,
      TPath,
      ListSuccessResponse<TData, TPath>
    >;
  };
}
